import { createBrowserRouter, createHashRouter, Navigate } from "react-router-dom";
import { lazy, Suspense, type ComponentType } from "react";
import { loadDemo } from "../demo/domain/load";
import { AskPage } from "../features/think/AskPage";
import { DecideRoom, TrustRoom, WindowRoom } from "../features/think/Rooms";

/*
 * The demo screens are lazy.
 *
 * They share a 37,000-line domain slice, and loading it to render the work
 * orders list would be absurd. Splitting here puts that slice in the chunks
 * that actually use it — the first demo screen pays for it, and every screen
 * after it is free.
 */
const load = (f: () => Promise<{ [k: string]: ComponentType }>, name: string) =>
  lazy(async () => ({ default: (await f())[name] }));

/**
 * A screen that brings the domain with it — and says so.
 *
 * The boards import `generated` statically, so opening one puts the whole slice
 * in memory. But `load.ts` records that a module is here only inside
 * `loadDemo()`, and nothing was calling it: a session that opened a board went
 * on believing the domain had never arrived. Everything queued through
 * `whenDemoLoaded` — the platform feed's merges, the facilities bar, the
 * sign-out entry in the activity log — then waited for the whole session while
 * the very screen it was waiting for was on the display. The boards drew the
 * sample estate beside a provenance line truthfully reporting nothing fed.
 *
 * The `loadDemo()` here resolves against the chunk the screen has already
 * pulled in, so it downloads nothing further. It only tells `load.ts` what is
 * already true, and awaiting it means the queue has flushed before the board
 * first draws.
 *
 * `load` stays for screens that do NOT import the domain — the instrument
 * bench draws gauges and nothing else, and making it announce a domain it never
 * imported would fetch three megabytes to render ten dials.
 */
const board = <P extends object = {}>(f: () => Promise<{ [k: string]: ComponentType<P> }>, name: string) =>
  lazy(async () => {
    const m = await f();
    await loadDemo();
    return { default: m[name] };
  });

const Dashboard = board<{ view?: string }>(() => import("../demo/boards/Dashboard"), "Dashboard");
const AirfieldMap = board<{ bar?: boolean; initialMode?: "drawing" | "airfield" | "day" | "night" }>(
  () => import("../demo/boards/AirfieldMap"),
  "AirfieldMap",
);
const RubberPlanner = board(() => import("../demo/boards/RubberPlanner"), "RubberPlanner");
const FrictionReview = board(() => import("../demo/boards/FrictionReview"), "FrictionReview");
const BuildingPlans = board(() => import("../demo/boards/BuildingPlans"), "BuildingPlans");
const SectionView3D = board(() => import("../demo/boards/SectionView3D"), "SectionView3D");
const InstrumentsPage = load(() => import("../demo/InstrumentsPage"), "InstrumentsPage");
const BuildingsScreen = board(() => import("../demo/boards/screens"), "BuildingsScreen");
const EngineeringScreen = board(() => import("../demo/boards/screens"), "EngineeringScreen");
const ProjectsScreen = board(() => import("../demo/boards/screens"), "ProjectsScreen");
const AssessmentsScreen = board(() => import("../demo/boards/screens"), "AssessmentsScreen");
const LandsideScreen = board(() => import("../demo/boards/screens"), "LandsideScreen");
const InspectionsScreen = board(() => import("../demo/boards/screens"), "InspectionsScreen");
/*
 * Through board(), not a static import, because it renders a Dashboard: the
 * boards import the demo domain statically, so one DRAWS either way — but
 * `load.ts` only learns the domain is here from `loadDemo()`, and everything
 * queued through `whenDemoLoaded` waits out the session otherwise. The stores
 * feed is queued that way, so a visitor whose first stop was this screen got
 * a board reporting that it was still awaiting the stores system.
 */
const LogisticsScreen = board(() => import("../features/stores/LogisticsScreen"), "LogisticsScreen");

/** Shown while a screen's chunk arrives; the shell around it stays put. */
const screen = (el: React.ReactNode) => (
  <Suspense
    fallback={
      <div className="empty" style={{ padding: 24 }}>
        Loading…
      </div>
    }
  >
    {el}
  </Suspense>
);

import { AppShell } from "../layout/AppShell";
import { DashboardPage } from "../features/dashboard/DashboardPage";
import { WorkOrdersPage } from "../features/work-orders/WorkOrdersPage";
import { WorkOrderDetailPage } from "../features/work-orders/WorkOrderDetailPage";
import { WorkOrderReportPage } from "../features/work-orders/WorkOrderReportPage";
import { EquipmentModulePage } from "../features/equipment/EquipmentModulePage";
import { FleetPage } from "../features/equipment/FleetPage";
import { AssetRegisterPage } from "../features/assetManagement/AssetRegisterPage";
import { AssetDetailPage } from "../features/assetManagement/AssetDetailPage";
import { AssetConditionPage } from "../features/assetManagement/AssetConditionPage";
import { AssetMaintenancePage } from "../features/assetManagement/AssetMaintenancePage";
import { AssetLifecyclePage } from "../features/assetManagement/AssetLifecyclePage";
import { HrCostPage } from "../features/hr/HrCostPage";
import { HrAccessPage } from "../features/hr/HrAccessPage";
import { HrAttendancePage } from "../features/hr/HrAttendancePage";
import { HrCalculatorsPage } from "../features/hr/HrCalculatorsPage";
import { HrHomePage } from "../features/hr/HrHomePage";
import { HrMePage } from "../features/hr/HrMePage";
import { HrMessagesPage } from "../features/hr/HrMessagesPage";
import { HrRecordsPage } from "../features/hr/HrRecordsPage";
import { HrReportsPage } from "../features/hr/HrReportsPage";
import { StaffFilePage } from "../features/hr/StaffFilePage";
import { HrLabourPage } from "../features/hr/HrLabourPage";
import { HrPeoplePage } from "../features/hr/HrPeoplePage";
import { HrRulesPage } from "../features/hr/HrRulesPage";
import { HrSaudizationPage } from "../features/hr/HrSaudizationPage";
import { FacilityInspectionsPage } from "../features/assets/FacilityInspectionsPage";
import { FleetHistoryPage } from "../features/equipment/FleetHistoryPage";
import { StoreItemPage } from "../features/stores/StoreItemPage";
import { InventoryPage } from "../features/stores/InventoryPage";
import { CounterPage } from "../features/stores/CounterPage";
import { FlightsPage } from "../features/flights/FlightsPage";
import { DelaysPage } from "../features/flights/DelaysPage";
import { AodbPage } from "../features/flights/AodbPage";
import { AllocationPage } from "../features/flights/AllocationPage";
import { FlowPage } from "../features/flights/FlowPage";
import { FlightFeedsPage } from "../features/flights/FlightFeedsPage";
import { VdgsPage } from "../features/vdgs/VdgsPage";
import { ContactsPage } from "../features/contacts/ContactsPage";
import { NotifyRulesPage } from "../features/contacts/NotifyRulesPage";
import { CallsPage } from "../features/calls/CallsPage";
import { CallPolicyPage } from "../features/calls/CallPolicyPage";
import { StandsPage } from "../features/flights/StandsPage";
import { TurnaroundsPage } from "../features/flights/TurnaroundsPage";
import { FidsPage } from "../features/flights/FidsPage";
import { TestingRecordsPage } from "../features/testing/TestingRecordsPage";
import { TestRecordDetailPage } from "../features/testing/TestRecordDetailPage";
import { EQUIPMENT_MODULES } from "../domain/estate";
import { DASHBOARD_SCREENS } from "../demo/boards/dashboardScreens";
import { StakeholdersScreen } from "../features/contracts/StakeholdersScreen";
import { AlertsPage } from "../features/alerts/AlertsPage";
import { InspectionsPage } from "../features/inspections/InspectionsPage";
import { InspectionDetailPage } from "../features/inspections/InspectionDetailPage";
import { MapPage } from "../features/map/MapPage";
import { CompliancePage } from "../features/compliance/CompliancePage";
import { LibraryPage } from "../features/library/LibraryPage";
import { ProjectDetailPage } from "../features/projects/ProjectDetailPage";
import { InspectionSheetPage } from "../features/projects/InspectionSheetPage";
import { ProjectsPage } from "../features/projects/ProjectsPage";
import { DocumentPage } from "../features/library/DocumentPage";
import { SettingsPage } from "../features/settings/SettingsPage";
import { IntelligenceReportPage } from "../features/intelligence/IntelligenceReportPage";
import { DBriefPage } from "../features/dbrief/DBriefPage";
import { DecisionAssistPage } from "../features/decision/DecisionAssistPage";
import { DecisionRoomPage } from "../features/decision/DecisionRoomPage";
import { BenchmarkPage } from "../features/executive/BenchmarkPage";
import { CapexPage } from "../features/executive/CapexPage";
import { CashPage } from "../features/executive/CashPage";
import { CrisisPage } from "../features/executive/CrisisPage";
import { UrgentInspectionPage } from "../features/urgent/UrgentInspectionPage";
import { ExpansionPage } from "../features/executive/ExpansionPage";
import { ImprovementPage } from "../features/executive/ImprovementPage";
import { RegulatoryPage } from "../features/executive/RegulatoryPage";
import { GreenPage } from "../features/green/GreenPage";
import { MetersPage } from "../features/meters/MetersPage";
import { MigrationPage } from "../features/migration/MigrationPage";
import { FinanceSystemPage } from "../features/finance-system/FinanceSystemPage";
import { AdministrationPage } from "../features/administration/AdministrationPage";

/**
 * One route per screen. Every screen the sidebar lists is served here; the
 * airfield application remains available for client demonstrations.
 */
/* Served at "/" in development and at "/app/" by the platform; Vite's BASE_URL carries which. */
const basename = import.meta.env.BASE_URL.replace(/\/$/, "") || undefined;

/*
 * Paths where there is a server, hashes where there is not.
 *
 * The published demo is a single HTML file opened from disk. A file:// page
 * has no origin and no server to route a path against: `pushState` is refused
 * outright, so a path router renders the first screen and then goes blank the
 * moment anybody navigates — which is exactly what a visitor does after
 * signing in. A hash router needs neither, and costs the hosted build
 * nothing, because the hosted build never takes this branch.
 *
 * Decided once, at load, from the protocol rather than from a build flag: a
 * flag can be forgotten when the demo is rebuilt, and has been.
 */
const fromDisk = typeof window !== "undefined" && window.location.protocol === "file:";
const createRouter = fromDisk ? createHashRouter : createBrowserRouter;

export const router = createRouter(
  [
    {
      path: "/",
      element: <AppShell />,
      children: [
        { index: true, element: <DashboardPage /> },
        { path: "work-orders", element: <WorkOrdersPage /> },
        { path: "work-orders/report", element: <WorkOrderReportPage /> },
        { path: "testing-records", element: <TestingRecordsPage /> },
        { path: "testing-records/:id", element: <TestRecordDetailPage /> },
        /* One screen per class of airfield equipment, all built from the same page. */
        ...EQUIPMENT_MODULES.map((m) => ({
          path: m.route.slice(1),
          element: <EquipmentModulePage key={m.key} module={m} />,
        })),
        { path: "work-orders/:id", element: <WorkOrderDetailPage /> },
        { path: "equipment", element: <FleetPage /> },
        { path: "asset-register", element: <AssetRegisterPage /> },
        { path: "asset-register/:id", element: <AssetDetailPage /> },
        { path: "asset-condition", element: <AssetConditionPage /> },
        { path: "asset-maintenance", element: <AssetMaintenancePage /> },
        { path: "asset-lifecycle", element: <AssetLifecyclePage /> },
        { path: "hr", element: <HrHomePage /> },
        { path: "hr-me", element: <HrMePage /> },
        { path: "hr-notifications", element: <HrMessagesPage /> },
        { path: "hr-people", element: <HrPeoplePage /> },
        { path: "hr-people/:id", element: <StaffFilePage /> },
        { path: "hr-attendance", element: <HrAttendancePage /> },
        { path: "hr-records", element: <HrRecordsPage /> },
        { path: "hr-calculators", element: <HrCalculatorsPage /> },
        { path: "hr-reports", element: <HrReportsPage /> },
        { path: "hr-access", element: <HrAccessPage /> },
        { path: "hr-cost", element: <HrCostPage /> },
        { path: "hr-labour", element: <HrLabourPage /> },
        { path: "hr-saudization", element: <HrSaudizationPage /> },
        { path: "hr-rules", element: <HrRulesPage /> },
        { path: "facility-inspections", element: <FacilityInspectionsPage /> },
        { path: "equipment/:tag", element: <FleetHistoryPage /> },
        /*
         * Store & Warehouse is one screen and lives at /logistics. This route
         * stays because the counter, the inventory, every item page and the
         * work-order parts card link into /stores/… — and anything already
         * pointing here should land on the screen, not on a dead path.
         */
        { path: "stores", element: screen(<LogisticsScreen />) },
        { path: "flights", element: <FlightsPage /> },
        { path: "flight-delays", element: <DelaysPage /> },
        { path: "aodb", element: <AodbPage /> },
        { path: "allocation", element: <AllocationPage /> },
        { path: "passenger-flow", element: <FlowPage /> },
        { path: "flight-feeds", element: <FlightFeedsPage /> },
        { path: "vdgs", element: <VdgsPage /> },
        { path: "contacts", element: <ContactsPage /> },
        { path: "notify-rules", element: <NotifyRulesPage /> },
        { path: "calls", element: <CallsPage /> },
        { path: "call-policy", element: <CallPolicyPage /> },
        { path: "stands", element: <StandsPage /> },
        { path: "turnarounds", element: <TurnaroundsPage /> },
        { path: "fids", element: <FidsPage /> },
        /* before :sku, or "inventory" is read as a catalogue number */
        { path: "stores/inventory", element: <InventoryPage /> },
        { path: "stores/counter", element: <CounterPage /> },
        { path: "stores/:sku", element: <StoreItemPage /> },
        { path: "airfield", element: <MapPage /> },
        { path: "alerts", element: <AlertsPage /> },
        { path: "inspections", element: <InspectionsPage /> },
        { path: "inspections/:id", element: <InspectionDetailPage /> },
        { path: "compliance", element: <CompliancePage /> },
        { path: "planning", element: <Navigate to="/window?tab=when" replace /> },
        { path: "liability", element: <Navigate to="/decide?tab=waiting" replace /> },
        { path: "capacity", element: <Navigate to="/window?tab=closure" replace /> },
        { path: "drift", element: <Navigate to="/trust?tab=models" replace /> },
        { path: "reliability", element: <Navigate to="/trust?tab=failures" replace /> },
        { path: "ledger", element: <Navigate to="/trust?tab=records" replace /> },
        { path: "weather", element: <Navigate to="/window?tab=weather" replace /> },
        { path: "programme", element: <Navigate to="/decide?tab=plan" replace /> },
        { path: "what-if", element: <Navigate to="/decide?tab=change" replace /> },
        /* Deep thinking: the Ask front door and three rooms of three tabs; the nine old addresses open the right tab */
        { path: "think", element: <AskPage /> },
        { path: "decide", element: <DecideRoom /> },
        { path: "window", element: <WindowRoom /> },
        { path: "trust", element: <TrustRoom /> },
        { path: "library", element: <LibraryPage /> },
        { path: "library/:id", element: <DocumentPage /> },
        { path: "settings", element: <SettingsPage /> },
        /* the strangler fig: what is being replaced, screen by screen, and on what evidence */
        { path: "migration", element: <MigrationPage /> },
        /* the finance system, read rather than uploaded — and what it would not count */
        { path: "finance-system", element: <FinanceSystemPage /> },
        /* the deeper settings: who is here, what they may do, and what this deployment still needs */
        { path: "administration", element: <AdministrationPage /> },
        { path: "intelligence-report", element: <IntelligenceReportPage /> },
        { path: "d-brief", element: <DBriefPage /> },
        { path: "decision-assist", element: <DecisionRoomPage /> },
        /* the engine beneath the room, kept reachable for whoever has to check the arithmetic */
        { path: "decision-assist/engine", element: <DecisionAssistPage /> },
        /* the executive suite: each a deterministic desk over the records, with a screen */
        { path: "crisis", element: <CrisisPage /> },
        { path: "urgent-inspection", element: <UrgentInspectionPage /> },
        { path: "cash", element: <CashPage /> },
        { path: "capacity-expansion", element: <ExpansionPage /> },
        { path: "capex", element: <CapexPage /> },
        { path: "regulatory", element: <RegulatoryPage /> },
        { path: "benchmark", element: <BenchmarkPage /> },
        { path: "improvement", element: <ImprovementPage /> },
        { path: "green-program", element: <GreenPage /> },
        { path: "meters", element: <MetersPage /> },
        /*
         * "capital-projects", not "projects": the ported Projects board already
         * holds /projects and is still what the demonstration shows. The same
         * split the map has, where /airfield is the platform's drawing and
         * /drawing is the demo's.
         *
         * The sheet is routed under the project rather than beside it, matching
         * the platform's own /v1/projects/inspections/{id}.
         */
        { path: "capital-projects", element: <ProjectsPage /> },
        { path: "capital-projects/inspections/:id", element: <InspectionSheetPage /> },
        { path: "capital-projects/:id", element: <ProjectDetailPage /> },
        /* the instrument bench — every gauge the boards are drawn with */
        { path: "instruments", element: screen(<InstrumentsPage />) },
        /* the ported Overview board */
        { path: "overview", element: screen(<Dashboard />) },
        /* each of its boards as a screen of its own: the dashboard, opened on that board */
        ...DASHBOARD_SCREENS.map((d) => ({
          path: d.to.slice(1),
          /*
           * Two boards keep their board and gain the register behind it: the
           * stakeholders board with the contracts, and the logistics board
           * with the stores it reports on. Both read as indicators alone
           * otherwise, with no way from a figure to the thing it describes.
           */
          element: screen(
            d.view === "stake" ? (
              <StakeholdersScreen />
            ) : d.view === "stores" ? (
              <LogisticsScreen />
            ) : (
              <Dashboard view={d.view} />
            ),
          ),
        })),
        /* the aerodrome drawing itself */
        /* the Operations Control dynamic map is this same screen; both open the same way */
        { path: "drawing", element: screen(<AirfieldMap bar initialMode="day" />) },
        { path: "rubber", element: screen(<RubberPlanner />) },
        { path: "friction", element: screen(<FrictionReview />) },
        { path: "buildings", element: screen(<BuildingsScreen />) },
        { path: "engineering", element: screen(<EngineeringScreen />) },
        { path: "projects", element: screen(<ProjectsScreen />) },
        { path: "assessments", element: screen(<AssessmentsScreen />) },
        { path: "landside", element: screen(<LandsideScreen />) },
        { path: "plans", element: screen(<BuildingPlans />) },
        { path: "section-3d", element: screen(<SectionView3D />) },
        { path: "inspection-platform", element: screen(<InspectionsScreen />) },
        { path: "*", element: <Navigate to="/" replace /> },
      ],
    },
  ],
  /* a hash router routes after the "#", so the served path is not its business */
  { basename: fromDisk ? undefined : basename },
);
